import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { LogOut, Plus, Edit, Trash2, BookOpen, Users, ClipboardList, Copy, Eye, TrendingUp, Flag, Settings, Upload, X, CalendarClock, KeyRound, FileText } from 'lucide-react';
import { toast } from 'sonner';

import { CreatePlanDialog, EditPlanDialog, ClonePlanDialog, TaskFormDialog, AssignPlanDialog, ManagePlansDialog, StartDateDialog as AdminStartDateDialog, MilestoneDialog, ExpirationDialog, BulkExpirationDialog, ResetPasswordDialog, CreateUserDialog } from '../components/admin/AdminDialogs';
import { RichTextEditor } from '../components/ui/RichTextEditor';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const countWordsFromHtml = (html) => {
  const plain = (html || '').replace(/<[^>]+>/g, '').replace(/&nbsp;/g, ' ').trim();
  if (!plain) return 0;
  return plain.split(/\s+/).length;
};

export const AdminDashboard = () => {
  const { user, logout, impersonate } = useAuth();
  const [studyPlans, setStudyPlans] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [taskSortOrder, setTaskSortOrder] = useState('asc');
  const [users, setUsers] = useState([]);
  const [selectedPlanId, setSelectedPlanId] = useState(null);
  
  // Study Plan Dialog
  const [showCreatePlanDialog, setShowCreatePlanDialog] = useState(false);
  const [showClonePlanDialog, setShowClonePlanDialog] = useState(false);
  const [planToClone, setPlanToClone] = useState(null);
  const [showEditPlanDialog, setShowEditPlanDialog] = useState(false);
  const [planToEdit, setPlanToEdit] = useState(null);
  const [planFormData, setPlanFormData] = useState({
    name: '',
    description: '',
    path_plan: ''
  });
  const [cloneFormData, setCloneFormData] = useState({
    new_name: '',
    new_description: ''
  });
  
  // Task Dialog
  const [showCreateTaskDialog, setShowCreateTaskDialog] = useState(false);
  const [showEditTaskDialog, setShowEditTaskDialog] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [taskFormData, setTaskFormData] = useState({
    activity: '',
    content: '',
    goals: '',
    days_offset: 0,
    status: 'open',
    category: null
  });
  
  // Assign Dialog
  const [showAssignDialog, setShowAssignDialog] = useState(false);
  const [showCreateUserDialog, setShowCreateUserDialog] = useState(false);
  const [createUserForm, setCreateUserForm] = useState({ name: '', email: '', password: '' });

  // Welcome Email
  const [welcomeEmailSubject, setWelcomeEmailSubject] = useState('');
  const [welcomeEmailBody, setWelcomeEmailBody] = useState('');
  const [welcomeEmailLoading, setWelcomeEmailLoading] = useState(false);
  const [testEmailAddress, setTestEmailAddress] = useState('');
  const [testEmailName, setTestEmailName] = useState('');
  const [emailLogs, setEmailLogs] = useState([]);
  const [loginLogs, setLoginLogs] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [selectedPlanForAssign, setSelectedPlanForAssign] = useState('');
  
  // Start Date Dialog
  const [showStartDateDialog, setShowStartDateDialog] = useState(false);
  const [userToEditStartDate, setUserToEditStartDate] = useState(null);
  const [newStartDate, setNewStartDate] = useState('');
  const [editingPlanId, setEditingPlanId] = useState(null);
  const [editingPlanName, setEditingPlanName] = useState('');

  // Manage Plans Dialog
  const [showManagePlansDialog, setShowManagePlansDialog] = useState(false);
  const [managePlansUser, setManagePlansUser] = useState(null);
  
  // View As User (now uses impersonation - no local state needed)
  
  // Analytics
  const [planAnalytics, setPlanAnalytics] = useState([]);
  const [taskAnalytics, setTaskAnalytics] = useState([]);

  // Milestones
  const [milestones, setMilestones] = useState([]);
  const [showCreateMilestoneDialog, setShowCreateMilestoneDialog] = useState(false);
  const [showEditMilestoneDialog, setShowEditMilestoneDialog] = useState(false);
  const [editingMilestone, setEditingMilestone] = useState(null);
  const [milestoneFormData, setMilestoneFormData] = useState({
    title: '',
    description: '',
    order: 0
  });

  // Welcome Notes
  const [welcomeNotes, setWelcomeNotes] = useState('');
  const [welcomeNotesLoading, setWelcomeNotesLoading] = useState(false);

  // Header Settings
  const [headerSettings, setHeaderSettings] = useState({
    logo_bg_color: '#1e40af',
    text_color: '#ffffff',
    logo_filename: null,
    logo_size: 80
  });
  const [logoPreview, setLogoPreview] = useState(null);
  const [logoFile, setLogoFile] = useState(null);

  // Sign-in page image
  const [signinImagePreview, setSigninImagePreview] = useState(null);

  // Sign-in page logo (separate from header logo)
  const [signinLogoPreview, setSigninLogoPreview] = useState(null);

  // User Task Analytics
  const [userTaskAnalytics, setUserTaskAnalytics] = useState([]);
  const [expandedUserId, setExpandedUserId] = useState(null);

  // Expiration Date
  const [showExpirationDialog, setShowExpirationDialog] = useState(false);
  const [expirationUser, setExpirationUser] = useState(null);
  const [expirationDate, setExpirationDate] = useState('');
  const [showBulkExpirationDialog, setShowBulkExpirationDialog] = useState(false);
  const [bulkExpirationDate, setBulkExpirationDate] = useState('');
  const [bulkExpirationPlanId, setBulkExpirationPlanId] = useState('');

  // Admin password reset
  const [showResetPasswordDialog, setShowResetPasswordDialog] = useState(false);
  const [resetPasswordUser, setResetPasswordUser] = useState(null);
  const [adminNewPassword, setAdminNewPassword] = useState('');
  const [showDeleteUserDialog, setShowDeleteUserDialog] = useState(false);
  const [deleteUserTarget, setDeleteUserTarget] = useState(null);
  const [showEditUserDialog, setShowEditUserDialog] = useState(false);
  const [editUserTarget, setEditUserTarget] = useState(null);
  const [editUserName, setEditUserName] = useState('');
  const [editUserEmail, setEditUserEmail] = useState('');

  const fetchStudyPlans = useCallback(async () => {
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/admin/study-plan-templates`, {
        withCredentials: true
      });
      setStudyPlans(data);
      if (data.length > 0 && !selectedPlanId) {
        setSelectedPlanId(data[0].id);
      }
    } catch (error) {
    }
  }, [selectedPlanId]);

  const fetchTasks = useCallback(async (planId) => {
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/admin/tasks?study_plan_template_id=${planId}`, {
        withCredentials: true
      });
      setTasks(data);
    } catch (error) {
    }
  }, []);

  const fetchUsers = useCallback(async () => {
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/admin/users`, {
        withCredentials: true
      });
      setUsers(data);
    } catch (error) {
    }
  }, []);

  const fetchAnalytics = useCallback(async () => {
    try {
      const [plansRes, tasksRes, userTasksRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/admin/analytics/overview`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/admin/analytics/tasks`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/admin/analytics/user-tasks`, { withCredentials: true })
      ]);
      setPlanAnalytics(plansRes.data);
      setTaskAnalytics(tasksRes.data);
      setUserTaskAnalytics(userTasksRes.data);
    } catch (error) {
    }
  }, []);

  const fetchHeaderSettings = useCallback(async () => {
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/settings/header`);
      if (data) {
        setHeaderSettings(prev => ({
          ...prev,
          logo_bg_color: data.logo_bg_color || '#1e40af',
          text_color: data.text_color || '#ffffff',
          logo_filename: data.logo_filename || null,
          logo_size: data.logo_size || 80
        }));
        if (data.logo_filename) {
          setLogoPreview(`${BACKEND_URL}/api/settings/logo?t=${Date.now()}`);
        }
        if (data.signin_image_filename) {
          setSigninImagePreview(`${BACKEND_URL}/api/settings/signin-image?t=${Date.now()}`);
        }
        if (data.signin_logo_filename) {
          setSigninLogoPreview(`${BACKEND_URL}/api/settings/signin-logo?t=${Date.now()}`);
        }
      }
    } catch (error) {
    }
  }, []);

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!['image/jpeg', 'image/png'].includes(file.type)) {
      toast.error('Only JPEG and PNG files are allowed');
      return;
    }
    setLogoFile(file);
    setLogoPreview(URL.createObjectURL(file));

    const formData = new FormData();
    formData.append('file', file);
    try {
      await axios.post(`${BACKEND_URL}/api/admin/settings/logo`, formData, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Logo uploaded!');
      fetchHeaderSettings();
    } catch (error) {
      const errorMsg = error.response?.data?.detail || error.message;
      toast.error(`Failed to upload logo: ${errorMsg}`);
    }
  };

  const handleRemoveLogo = async () => {
    try {
      await axios.delete(`${BACKEND_URL}/api/admin/settings/logo`, { withCredentials: true });
      setLogoPreview(null);
      setLogoFile(null);
      setHeaderSettings(prev => ({ ...prev, logo_filename: null }));
      toast.success('Logo removed');
    } catch (error) {
      toast.error('Failed to remove logo');
    }
  };

  const handleSigninImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!['image/jpeg', 'image/png'].includes(file.type)) {
      toast.error('Only JPEG and PNG files are allowed');
      return;
    }
    const formData = new FormData();
    formData.append('file', file);
    try {
      await axios.post(`${BACKEND_URL}/api/admin/settings/signin-image`, formData, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setSigninImagePreview(URL.createObjectURL(file));
      toast.success('Sign-in image uploaded!');
    } catch (error) {
      const errorMsg = error.response?.data?.detail || error.message;
      toast.error(`Failed to upload: ${errorMsg}`);
    }
  };

  const handleRemoveSigninImage = async () => {
    try {
      await axios.delete(`${BACKEND_URL}/api/admin/settings/signin-image`, { withCredentials: true });
      setSigninImagePreview(null);
      toast.success('Sign-in image removed');
    } catch (error) {
      toast.error('Failed to remove sign-in image');
    }
  };

  const handleSigninLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!['image/jpeg', 'image/png'].includes(file.type)) {
      toast.error('Only JPEG and PNG files are allowed');
      return;
    }
    const formData = new FormData();
    formData.append('file', file);
    try {
      await axios.post(`${BACKEND_URL}/api/admin/settings/signin-logo`, formData, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setSigninLogoPreview(URL.createObjectURL(file));
      toast.success('Sign-in logo uploaded!');
    } catch (error) {
      const errorMsg = error.response?.data?.detail || error.message;
      toast.error(`Failed to upload: ${errorMsg}`);
    }
  };

  const handleRemoveSigninLogo = async () => {
    try {
      await axios.delete(`${BACKEND_URL}/api/admin/settings/signin-logo`, { withCredentials: true });
      setSigninLogoPreview(null);
      toast.success('Sign-in logo removed');
    } catch (error) {
      toast.error('Failed to remove sign-in logo');
    }
  };

  const handleSaveHeaderSettings = async () => {
    try {
      await axios.put(
        `${BACKEND_URL}/api/admin/settings/header`,
        { logo_bg_color: headerSettings.logo_bg_color, text_color: headerSettings.text_color, logo_size: headerSettings.logo_size },
        { withCredentials: true }
      );
      toast.success('Header settings saved!');
    } catch (error) {
      const errorMsg = error.response?.data?.detail || error.message;
      toast.error(`Failed to save settings: ${errorMsg}`);
    }
  };

  const fetchMilestones = useCallback(async (planId) => {
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/admin/milestones?study_plan_template_id=${planId}`, {
        withCredentials: true
      });
      setMilestones(data);
    } catch (error) {
    }
  }, []);

  const fetchWelcomeNotes = useCallback(async (planId) => {
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/admin/study-plan-templates/${planId}/welcome-notes`, { withCredentials: true });
      setWelcomeNotes(data.welcome_notes || '');
    } catch { setWelcomeNotes(''); }
  }, []);

  const handleSaveWelcomeNotes = async () => {
    if (!selectedPlanId) return;
    setWelcomeNotesLoading(true);
    try {
      await axios.put(`${BACKEND_URL}/api/admin/study-plan-templates/${selectedPlanId}/welcome-notes`, { welcome_notes: welcomeNotes }, { withCredentials: true });
      toast.success('Welcome notes saved!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save notes');
    }
    setWelcomeNotesLoading(false);
  };

  const fetchWelcomeEmailSettings = async () => {
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/admin/settings/welcome-email`, { withCredentials: true });
      setWelcomeEmailSubject(data.subject || '');
      // Normalize body: convert markdown to proper HTML
      let body = data.body || '';
      // Convert **markdown** bold to <strong>
      body = body.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
      // If body is mostly plain text (no block-level HTML), convert it
      if (body && !body.match(/<(p|div|ul|ol|li|br)\b/i)) {
        body = body.split('\n\n').map(p => `<p>${p.replace(/\n/g, '<br>')}</p>`).join('');
      }
      // Convert plain-text bullet lines (- item or * item) to <ul><li>
      body = body.replace(/((?:^|\n|<br\/?>)\s*[-*]\s+.+(?:\n|<br\/?>)?)+/g, (match) => {
        const items = match.split(/\n|<br\/?>/).filter(l => l.trim().match(/^[-*]\s+/)).map(l => {
          return `<li>${l.trim().replace(/^[-*]\s+/, '')}</li>`;
        });
        return items.length ? `<ul>${items.join('')}</ul>` : match;
      });
      setWelcomeEmailBody(body);
    } catch (error) { /* settings may not exist yet */ }
  };

  const fetchEmailLogs = async () => {
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/admin/email-logs`, { withCredentials: true });
      setEmailLogs(data);
    } catch (error) { /* logs may not exist yet */ }
  };

  const fetchLoginLogs = async () => {
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/admin/login-logs`, { withCredentials: true });
      setLoginLogs(data);
    } catch { /* logs may not exist yet */ }
  };


  const handleSaveWelcomeEmail = async () => {
    if (!welcomeEmailSubject.trim() || !welcomeEmailBody.trim()) {
      toast.error('Subject and body are required'); return;
    }
    setWelcomeEmailLoading(true);
    try {
      await axios.put(`${BACKEND_URL}/api/admin/settings/welcome-email`,
        { subject: welcomeEmailSubject, body: welcomeEmailBody },
        { withCredentials: true }
      );
      toast.success('Welcome email template saved');
    } catch (error) {
      toast.error(`Failed: ${error.response?.data?.detail || error.message}`);
    } finally { setWelcomeEmailLoading(false); }
  };

  const handleSendTestEmail = async () => {
    if (!testEmailAddress.trim()) { toast.error('Enter a recipient email address'); return; }
    setWelcomeEmailLoading(true);
    try {
      await axios.post(`${BACKEND_URL}/api/admin/settings/welcome-email/send`,
        { to_email: testEmailAddress, to_name: testEmailName || 'User' },
        { withCredentials: true }
      );
      toast.success(`Welcome email sent to ${testEmailAddress}`);
      setTestEmailAddress('');
      setTestEmailName('');
      fetchEmailLogs();
    } catch (error) {
      toast.error(`Failed: ${error.response?.data?.detail || error.message}`);
    } finally { setWelcomeEmailLoading(false); }
  };

  useEffect(() => {
    fetchStudyPlans();
    fetchUsers();
    fetchAnalytics();
    fetchHeaderSettings();
    fetchWelcomeEmailSettings();
    fetchEmailLogs();
    fetchLoginLogs();
  }, [fetchStudyPlans, fetchUsers, fetchAnalytics, fetchHeaderSettings]);

  useEffect(() => {
    if (selectedPlanId) {
      fetchTasks(selectedPlanId);
      fetchMilestones(selectedPlanId);
      fetchWelcomeNotes(selectedPlanId);
    }
  }, [selectedPlanId, fetchTasks, fetchMilestones, fetchWelcomeNotes]);

  const handleCreateMilestone = async () => {
    if (!selectedPlanId) {
      toast.error('Please select a study plan first');
      return;
    }
    if (!milestoneFormData.title.trim()) {
      toast.error('Milestone title is required');
      return;
    }
    try {
      await axios.post(
        `${BACKEND_URL}/api/admin/milestones`,
        { ...milestoneFormData, study_plan_template_id: selectedPlanId },
        { withCredentials: true }
      );
      toast.success('Milestone created!');
      setShowCreateMilestoneDialog(false);
      setMilestoneFormData({ title: '', description: '', order: 0 });
      fetchMilestones(selectedPlanId);
    } catch (error) {
      const errorMsg = error.response?.data?.detail || error.message || 'Unknown error';
      toast.error(`Failed to create milestone: ${errorMsg}`);
    }
  };

  const handleUpdateMilestone = async () => {
    try {
      await axios.put(
        `${BACKEND_URL}/api/admin/milestones/${editingMilestone.id}`,
        milestoneFormData,
        { withCredentials: true }
      );
      toast.success('Milestone updated for all users!');
      setShowEditMilestoneDialog(false);
      setEditingMilestone(null);
      setMilestoneFormData({ title: '', description: '', order: 0 });
      fetchMilestones(selectedPlanId);
    } catch (error) {
      const errorMsg = error.response?.data?.detail || error.message || 'Unknown error';
      toast.error(`Failed to update milestone: ${errorMsg}`);
    }
  };

  const handleDeleteMilestone = async (milestoneId) => {
    if (!window.confirm('Delete this milestone?')) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/admin/milestones/${milestoneId}`, {
        withCredentials: true
      });
      toast.success('Milestone deleted!');
      fetchMilestones(selectedPlanId);
    } catch (error) {
      const errorMsg = error.response?.data?.detail || error.message || 'Unknown error';
      toast.error(`Failed to delete milestone: ${errorMsg}`);
    }
  };

  const openEditMilestoneDialog = (milestone) => {
    setEditingMilestone(milestone);
    setMilestoneFormData({
      title: milestone.title,
      description: milestone.description || '',
      order: milestone.order || 0
    });
    setShowEditMilestoneDialog(true);
  };

  const openExpirationDialog = (user, plan) => {
    setExpirationUser(user);
    setEditingPlanId(plan.study_plan_template_id);
    setEditingPlanName(plan.study_plan_name);
    setExpirationDate(plan.expiration_date || '');
    setShowExpirationDialog(true);
  };

  const handleSetExpiration = async () => {
    try {
      await axios.put(
        `${BACKEND_URL}/api/admin/users/${expirationUser.id}/plans/${editingPlanId}/expiration-date`,
        { expiration_date: expirationDate || null },
        { withCredentials: true }
      );
      toast.success('Expiration date updated');
      setShowExpirationDialog(false);
      fetchUsers();
    } catch (error) {
      toast.error(`Failed: ${error.response?.data?.detail || error.message}`);
    }
  };

  const handleBulkExpiration = async () => {
    if (!bulkExpirationPlanId) {
      toast.error('Select a study plan');
      return;
    }
    try {
      const { data } = await axios.put(
        `${BACKEND_URL}/api/admin/bulk-expiration`,
        { study_plan_template_id: bulkExpirationPlanId, expiration_date: bulkExpirationDate || null },
        { withCredentials: true }
      );
      toast.success(`Expiration updated for ${data.updated_count} users`);
      setShowBulkExpirationDialog(false);
      setBulkExpirationDate('');
      setBulkExpirationPlanId('');
      fetchUsers();
    } catch (error) {
      toast.error(`Failed: ${error.response?.data?.detail || error.message}`);
    }
  };

  const handleAdminResetPassword = async () => {
    if (!adminNewPassword || adminNewPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    try {
      await axios.put(
        `${BACKEND_URL}/api/admin/users/${resetPasswordUser.id}/reset-password`,
        { new_password: adminNewPassword },
        { withCredentials: true }
      );
      toast.success(`Password reset for ${resetPasswordUser.email}`);
      setShowResetPasswordDialog(false);
      setResetPasswordUser(null);
      setAdminNewPassword('');
    } catch (error) {
      toast.error(`Failed: ${error.response?.data?.detail || error.message}`);
    }
  };

  const handleDeleteUser = async () => {
    if (!deleteUserTarget) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/admin/users/${deleteUserTarget.id}`, { withCredentials: true });
      toast.success(`User ${deleteUserTarget.name} deleted successfully`);
      setShowDeleteUserDialog(false);
      setDeleteUserTarget(null);
      fetchUsers();
    } catch (error) {
      toast.error(`Failed: ${error.response?.data?.detail || error.message}`);
    }
  };

  const openEditUser = (u) => {
    setEditUserTarget(u);
    setEditUserName(u.name);
    setEditUserEmail(u.email);
    setShowEditUserDialog(true);
  };

  const handleEditUser = async () => {
    if (!editUserTarget) return;
    try {
      await axios.put(`${BACKEND_URL}/api/admin/users/${editUserTarget.id}`, { name: editUserName, email: editUserEmail }, { withCredentials: true });
      toast.success('User updated successfully');
      setShowEditUserDialog(false);
      setEditUserTarget(null);
      fetchUsers();
    } catch (error) {
      toast.error(`Failed: ${error.response?.data?.detail || error.message}`);
    }
  };


  const handleCreateStudyPlan = async () => {
    try {
      await axios.post(
        `${BACKEND_URL}/api/admin/study-plan-templates`,
        planFormData,
        { withCredentials: true }
      );
      toast.success('Study plan template created!');
      setShowCreatePlanDialog(false);
      resetPlanForm();
      fetchStudyPlans();
    } catch (error) {
      const errorMsg = error.response?.data?.detail || error.message || 'Unknown error';
      toast.error(`Failed to create study plan: ${errorMsg}`);
    }
  };

  const openEditPlanDialog = (plan) => {
    setPlanToEdit(plan);
    setPlanFormData({ name: plan.name, description: plan.description, path_plan: plan.path_plan || '' });
    setShowEditPlanDialog(true);
  };

  const handleUpdateStudyPlan = async () => {
    if (!planToEdit) return;
    try {
      await axios.put(
        `${BACKEND_URL}/api/admin/study-plan-templates/${planToEdit.id}`,
        planFormData,
        { withCredentials: true }
      );
      toast.success('Study plan updated!');
      setShowEditPlanDialog(false);
      setPlanToEdit(null);
      resetPlanForm();
      fetchStudyPlans();
    } catch (error) {
      const errorMsg = error.response?.data?.detail || error.message || 'Unknown error';
      toast.error(`Failed to update study plan: ${errorMsg}`);
    }
  };

  const handleDeleteStudyPlan = async (planId) => {
    if (!window.confirm('Delete this study plan and all its tasks?')) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/admin/study-plan-templates/${planId}`, {
        withCredentials: true
      });
      toast.success('Study plan deleted!');
      fetchStudyPlans();
      setSelectedPlanId(null);
    } catch (error) {
      const errorMsg = error.response?.data?.detail || error.message || 'Unknown error';
      toast.error(`Failed to delete: ${errorMsg}`);
    }
  };

  const openCloneDialog = (plan) => {
    setPlanToClone(plan);
    setCloneFormData({
      new_name: `${plan.name} (Copy)`,
      new_description: plan.description
    });
    setShowClonePlanDialog(true);
  };

  const handleCloneStudyPlan = async () => {
    if (!planToClone || !cloneFormData.new_name) {
      toast.error('Please provide a name for the cloned study plan');
      return;
    }
    try {
      const { data } = await axios.post(
        `${BACKEND_URL}/api/admin/study-plan-templates/${planToClone.id}/clone`,
        cloneFormData,
        { withCredentials: true }
      );
      toast.success(`Study plan cloned! ${data.tasks_cloned} tasks copied.`);
      setShowClonePlanDialog(false);
      setPlanToClone(null);
      setCloneFormData({ new_name: '', new_description: '' });
      fetchStudyPlans();
    } catch (error) {
      const errorMsg = error.response?.data?.detail || error.message || 'Unknown error';
      toast.error(`Failed to clone: ${errorMsg}`);
    }
  };

  const handleCreateTask = async () => {
    if (!selectedPlanId) {
      toast.error('Please select a study plan first');
      return;
    }
    try {
      await axios.post(
        `${BACKEND_URL}/api/admin/tasks`,
        { ...taskFormData, study_plan_template_id: selectedPlanId },
        { withCredentials: true }
      );
      toast.success('Task created and distributed!');
      setShowCreateTaskDialog(false);
      resetTaskForm();
      fetchTasks(selectedPlanId);
    } catch (error) {
      const errorMsg = error.response?.data?.detail || error.message || 'Unknown error';
      toast.error(`Failed to create task: ${errorMsg}`);
    }
  };

  const handleUpdateTask = async () => {
    try {
      await axios.put(
        `${BACKEND_URL}/api/admin/tasks/${editingTask.id}`,
        taskFormData,
        { withCredentials: true }
      );
      toast.success('Task updated across all users!');
      setShowEditTaskDialog(false);
      setEditingTask(null);
      resetTaskForm();
      fetchTasks(selectedPlanId);
    } catch (error) {
      const errorMsg = error.response?.data?.detail || error.message || 'Unknown error';
      toast.error(`Failed to update task: ${errorMsg}`);
    }
  };

  const handleDeleteTask = async (taskId) => {
    if (!window.confirm('Delete this task from all users?')) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/admin/tasks/${taskId}`, {
        withCredentials: true
      });
      toast.success('Task deleted!');
      fetchTasks(selectedPlanId);
    } catch (error) {
      const errorMsg = error.response?.data?.detail || error.message || 'Unknown error';
      toast.error(`Failed to delete: ${errorMsg}`);
    }
  };

  const handleDuplicateTask = async (taskId) => {
    try {
      const { data } = await axios.post(
        `${BACKEND_URL}/api/admin/tasks/${taskId}/duplicate`,
        {},
        { withCredentials: true }
      );
      toast.success('Task duplicated! You can now modify it.');
      fetchTasks(selectedPlanId);
      setTimeout(() => {
        openEditTaskDialog(data);
      }, 500);
    } catch (error) {
      const errorMsg = error.response?.data?.detail || error.message || 'Unknown error';
      toast.error(`Failed to duplicate: ${errorMsg}`);
    }
  };

  const handleAssignPlan = async (categories) => {
    if (!selectedUserId || !selectedPlanForAssign) {
      toast.error('Please select both user and study plan');
      return;
    }
    try {
      const payload = {
        user_id: selectedUserId,
        study_plan_template_id: selectedPlanForAssign
      };
      if (categories && categories.length > 0) {
        payload.categories = categories;
      }
      const { data } = await axios.post(
        `${BACKEND_URL}/api/admin/assign-study-plan`,
        payload,
        { withCredentials: true }
      );
      toast.success(`Study plan assigned! ${data.tasks_created} tasks created for user.`);
      setShowAssignDialog(false);
      setSelectedUserId('');
      setSelectedPlanForAssign('');
      fetchUsers();
    } catch (error) {
      const errorMsg = error.response?.data?.detail || error.message || 'Unknown error';
      toast.error(`Failed to assign: ${errorMsg}`);
    }
  };

  const handleCreateUser = async () => {
    if (!createUserForm.name.trim() || !createUserForm.email.trim() || !createUserForm.password.trim()) {
      toast.error('Please fill in all fields');
      return;
    }
    try {
      await axios.post(`${BACKEND_URL}/api/admin/users`, createUserForm, { withCredentials: true });
      toast.success('User created successfully!');
      setShowCreateUserDialog(false);
      setCreateUserForm({ name: '', email: '', password: '' });
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create user');
    }
  };


  const openStartDateDialog = (user, plan) => {
    setUserToEditStartDate(user);
    setEditingPlanId(plan.study_plan_template_id);
    setEditingPlanName(plan.study_plan_name);
    setNewStartDate(plan.start_date || '');
    setShowStartDateDialog(true);
  };

  const handleUpdateUserStartDate = async () => {
    if (!newStartDate) {
      toast.error('Please select a start date');
      return;
    }
    try {
      const { data } = await axios.put(
        `${BACKEND_URL}/api/admin/users/${userToEditStartDate.id}/plans/${editingPlanId}/start-date`,
        { start_date: newStartDate },
        { withCredentials: true }
      );
      toast.success(`Start date updated! ${data.tasks_updated} task due dates recalculated.`);
      setShowStartDateDialog(false);
      setUserToEditStartDate(null);
      setNewStartDate('');
      fetchUsers();
    } catch (error) {
      const errorMsg = error.response?.data?.detail || error.message || 'Unknown error';
      toast.error(`Failed to update start date: ${errorMsg}`);
    }
  };

  const handleRemovePlan = async (userId, planId, planName) => {
    if (!window.confirm(`Remove "${planName}" from this user? All tasks for this plan will be deleted.`)) return;
    try {
      await axios.post(
        `${BACKEND_URL}/api/admin/remove-study-plan`,
        { user_id: userId, study_plan_template_id: planId },
        { withCredentials: true }
      );
      toast.success(`Plan "${planName}" removed`);
      fetchUsers();
      // Update the manage plans dialog if open
      setManagePlansUser(prev => {
        if (!prev || prev.id !== userId) return prev;
        return { ...prev, assigned_plans: prev.assigned_plans.filter(p => p.study_plan_template_id !== planId) };
      });
    } catch (error) {
      toast.error(`Failed: ${error.response?.data?.detail || error.message}`);
    }
  };

  const openViewAsUser = async (u) => {
    const result = await impersonate(u.id);
    if (!result.success) {
      toast.error(`Failed to impersonate: ${result.error}`);
    }
  };

  const openEditTaskDialog = (task) => {
    setEditingTask(task);
    setTaskFormData({
      activity: task.activity,
      content: task.content,
      goals: task.goals,
      days_offset: task.days_offset,
      status: task.status,
      category: task.category || null
    });
    setShowEditTaskDialog(true);
  };

  const resetPlanForm = () => {
    setPlanFormData({ name: '', description: '', path_plan: '' });
  };

  const resetTaskForm = () => {
    setTaskFormData({
      activity: '',
      content: '',
      goals: '',
      days_offset: 0,
      status: 'open'
    });
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="h-14 sm:h-16 bg-white border-b border-slate-200 flex items-center justify-between px-3 sm:px-6 sticky top-0 z-30">
        <h1 className="text-xl sm:text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
          Admin Dashboard
        </h1>
        <div className="flex items-center gap-2 sm:gap-4">
          <span className="text-xs sm:text-sm text-slate-600 hidden sm:inline" style={{ fontFamily: 'Figtree, sans-serif' }}>
            <strong>{user?.name}</strong> (Admin)
          </span>
          <Button 
            onClick={logout} 
            variant="ghost" 
            size="sm"
            className="text-slate-600 hover:text-slate-900"
            data-testid="admin-logout-button"
          >
            <LogOut className="w-4 h-4 sm:mr-2" />
            <span className="hidden sm:inline">Logout</span>
          </Button>
        </div>
      </header>

      <main className="p-4 sm:p-6 lg:p-8 max-w-7xl mx-auto">
        <Tabs defaultValue="plans" className="w-full">
          <TabsList className="mb-6 flex-wrap h-auto gap-1">
            <TabsTrigger value="plans" data-testid="plans-tab">
              <BookOpen className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline">Study Plans</span>
              <span className="sm:hidden text-xs">Plans</span>
            </TabsTrigger>
            <TabsTrigger value="tasks" data-testid="tasks-tab">
              <ClipboardList className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline">Tasks</span>
              <span className="sm:hidden text-xs">Tasks</span>
            </TabsTrigger>
            <TabsTrigger value="users" data-testid="users-tab">
              <Users className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline">Users</span>
              <span className="sm:hidden text-xs">Users</span>
            </TabsTrigger>
            <TabsTrigger value="analytics" data-testid="analytics-tab">
              <TrendingUp className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline">Analytics</span>
              <span className="sm:hidden text-xs">Stats</span>
            </TabsTrigger>
            <TabsTrigger value="milestones" data-testid="milestones-tab">
              <Flag className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline">Milestones</span>
              <span className="sm:hidden text-xs">Miles.</span>
            </TabsTrigger>
            <TabsTrigger value="notes" data-testid="notes-tab">
              <FileText className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline">Notes</span>
              <span className="sm:hidden text-xs">Notes</span>
            </TabsTrigger>
            <TabsTrigger value="settings" data-testid="settings-tab">
              <Settings className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline">Settings</span>
              <span className="sm:hidden text-xs">Set.</span>
            </TabsTrigger>
          </TabsList>

          <TabsContent value="plans">
            <div className="bg-white border border-slate-200 rounded-xl p-4 sm:p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  Study Plan Templates
                </h2>
                <Button 
                  onClick={() => setShowCreatePlanDialog(true)}
                  className="bg-blue-600 hover:bg-blue-700"
                  data-testid="create-plan-button"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Create Study Plan
                </Button>
              </div>

              {studyPlans.length === 0 ? (
                <div className="text-center py-12">
                  <p className="text-slate-500">No study plans yet. Create your first one!</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {studyPlans.map((plan) => (
                    <div 
                      key={plan.id} 
                      className={`border rounded-lg p-4 cursor-pointer transition-all ${
                        selectedPlanId === plan.id 
                          ? 'border-blue-500 bg-blue-50' 
                          : 'border-slate-200 hover:border-blue-300'
                      }`}
                      onClick={() => setSelectedPlanId(plan.id)}
                      data-testid={`plan-card-${plan.id}`}
                    >
                      <h3 className="font-semibold text-lg mb-2">{plan.name}</h3>
                      <p className="text-sm text-slate-600 mb-2">{plan.description}</p>
                      {plan.path_plan && (
                        <p className="text-xs text-blue-700 bg-blue-50 border border-blue-200 rounded px-2 py-1 mb-4" data-testid={`plan-path-${plan.id}`}>
                          <span className="font-semibold">Study Path Plan:</span> {plan.path_plan}
                        </p>
                      )}
                      {!plan.path_plan && <div className="mb-4" />}
                      <div className="flex gap-2">
                        <Button 
                          size="sm" 
                          variant="outline"
                          className="text-slate-600 hover:text-slate-700"
                          onClick={(e) => {
                            e.stopPropagation();
                            openEditPlanDialog(plan);
                          }}
                          data-testid={`edit-plan-${plan.id}`}
                        >
                          <Edit className="w-4 h-4 mr-1" />
                          Edit
                        </Button>
                        <Button 
                          size="sm" 
                          variant="outline"
                          className="text-blue-600 hover:text-blue-700"
                          onClick={(e) => {
                            e.stopPropagation();
                            openCloneDialog(plan);
                          }}
                          data-testid={`clone-plan-${plan.id}`}
                        >
                          <Copy className="w-4 h-4 mr-1" />
                          Clone
                        </Button>
                        <Button 
                          size="sm" 
                          variant="outline"
                          className="text-red-600 hover:text-red-700"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteStudyPlan(plan.id);
                          }}
                          data-testid={`delete-plan-${plan.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>

          <TabsContent value="tasks">
            <div className="bg-white border border-slate-200 rounded-xl p-4 sm:p-6">
              <div className="flex justify-between items-center mb-6">
                <div>
                  <h2 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    Task Templates
                  </h2>
                  {selectedPlanId && (
                    <p className="text-sm text-slate-600 mt-1">
                      For: {studyPlans.find(p => p.id === selectedPlanId)?.name}
                    </p>
                  )}
                </div>
                <Button 
                  onClick={() => setShowCreateTaskDialog(true)}
                  className="bg-blue-600 hover:bg-blue-700"
                  disabled={!selectedPlanId}
                  data-testid="create-task-button"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Create Task
                </Button>
              </div>

              {(() => {
                if (!selectedPlanId) return (
                  <div className="text-center py-12">
                    <p className="text-slate-500">Select a study plan from the Plans tab first</p>
                  </div>
                );
                if (tasks.length === 0) return (
                  <div className="text-center py-12">
                    <p className="text-slate-500">No tasks yet. Create your first task!</p>
                  </div>
                );
                return (
                <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Activity</TableHead>
                      <TableHead>Content</TableHead>
                      <TableHead>Goals</TableHead>
                      <TableHead className="cursor-pointer select-none" onClick={() => {
                        setTaskSortOrder(prev => prev === 'asc' ? 'desc' : 'asc');
                      }} data-testid="sort-days-offset">
                        <span className="flex items-center gap-1">Days Offset {taskSortOrder === 'asc' ? '↑' : '↓'}</span>
                      </TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {[...tasks].sort((a, b) => taskSortOrder === 'asc' ? a.days_offset - b.days_offset : b.days_offset - a.days_offset).map((task) => (
                      <TableRow key={task.id} data-testid={`task-row-${task.id}`}>
                        <TableCell className="font-medium">
                          {task.activity}
                          {task.category && (
                            <span className="block text-xs text-blue-600 font-normal mt-0.5">{task.category}</span>
                          )}
                        </TableCell>
                        <TableCell className="max-w-xs truncate">{task.content}</TableCell>
                        <TableCell className="max-w-xs truncate">{task.goals}</TableCell>
                        <TableCell>+{task.days_offset} days</TableCell>
                        <TableCell>
                          <span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold ${
                            task.status === 'open' 
                              ? 'bg-blue-100 text-blue-800 border border-blue-200' 
                              : 'bg-slate-100 text-slate-800 border border-slate-200'
                          }`}>
                            {task.status}
                          </span>
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-2">
                            <Button 
                              size="sm" 
                              variant="outline"
                              onClick={() => openEditTaskDialog(task)}
                              data-testid={`edit-task-${task.id}`}
                            >
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button 
                              size="sm" 
                              variant="outline"
                              className="text-blue-600 hover:text-blue-700"
                              onClick={() => handleDuplicateTask(task.id)}
                              data-testid={`duplicate-task-${task.id}`}
                            >
                              <Copy className="w-4 h-4" />
                            </Button>
                            <Button 
                              size="sm" 
                              variant="outline"
                              className="text-red-600 hover:text-red-700"
                              onClick={() => handleDeleteTask(task.id)}
                              data-testid={`delete-task-${task.id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                </div>
                );
              })()}
            </div>
          </TabsContent>

          <TabsContent value="users">
            <div className="bg-white border border-slate-200 rounded-xl p-4 sm:p-6">
              <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 mb-6">
                <h2 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  Manage Users
                </h2>
                <div className="flex gap-2">
                  <Button
                    onClick={() => setShowCreateUserDialog(true)}
                    className="bg-blue-600 hover:bg-blue-700"
                    data-testid="create-user-button"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Add User
                  </Button>
                  <Button
                    onClick={() => setShowBulkExpirationDialog(true)}
                    variant="outline"
                    data-testid="bulk-expiration-button"
                  >
                    <CalendarClock className="w-4 h-4 mr-2" />
                    Bulk Expiration
                  </Button>
                  <Button 
                    onClick={() => setShowAssignDialog(true)}
                    className="bg-blue-600 hover:bg-blue-700"
                    data-testid="assign-plan-button"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Assign Plan
                  </Button>
                </div>
              </div>

              {users.length === 0 ? (
                <div className="text-center py-12">
                  <p className="text-slate-500">No users registered yet.</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>Assigned Plans</TableHead>
                      <TableHead>Joined</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {users.map((u) => {
                      const plans = u.assigned_plans || [];
                      return (
                      <TableRow key={u.id}>
                        <TableCell className="font-medium">{u.name}</TableCell>
                        <TableCell>{u.email}</TableCell>
                        <TableCell>
                          {plans.length > 0 ? (
                            <div className="flex flex-wrap gap-1">
                              {plans.map((p) => {
                                const isExpired = p.expiration_date && new Date(p.expiration_date) < new Date();
                                return (
                                  <span key={p.study_plan_template_id} className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
                                    isExpired ? 'bg-red-100 text-red-800 border border-red-200'
                                    : 'bg-green-100 text-green-800 border border-green-200'
                                  }`} data-testid={`plan-badge-${p.study_plan_template_id}`}>
                                    {p.study_plan_name}
                                  </span>
                                );
                              })}
                            </div>
                          ) : (
                            <span className="text-slate-400 text-sm">Not assigned</span>
                          )}
                        </TableCell>
                        <TableCell>{new Date(u.created_at).toLocaleDateString()}</TableCell>
                        <TableCell>
                          <div className="flex gap-2">
                            <Button 
                              size="sm" 
                              variant="outline"
                              onClick={() => { setManagePlansUser(u); setShowManagePlansDialog(true); }}
                              data-testid={`manage-plans-${u.id}`}
                            >
                              <ClipboardList className="w-4 h-4 mr-1" />
                              Plans ({plans.length})
                            </Button>
                            <Button 
                              size="sm" 
                              variant="outline"
                              className="text-blue-600 hover:text-blue-700"
                              onClick={() => openViewAsUser(u)}
                              data-testid={`view-as-user-${u.id}`}
                            >
                              <Eye className="w-4 h-4 mr-1" />
                              View
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              className="text-orange-600 hover:text-orange-700"
                              onClick={() => { setResetPasswordUser(u); setAdminNewPassword(''); setShowResetPasswordDialog(true); }}
                              data-testid={`reset-password-${u.id}`}
                            >
                              <KeyRound className="w-4 h-4 mr-1" />
                              Reset PW
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              className="text-slate-600 hover:text-slate-700"
                              onClick={() => openEditUser(u)}
                              data-testid={`edit-user-${u.id}`}
                            >
                              <Edit className="w-4 h-4 mr-1" />
                              Edit
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              className="text-red-600 hover:text-red-700"
                              onClick={() => { setDeleteUserTarget(u); setShowDeleteUserDialog(true); }}
                              data-testid={`delete-user-${u.id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
                </div>
              )}
            </div>
          </TabsContent>

          <TabsContent value="analytics">
            <div className="space-y-6">
              {/* Grand Totals Summary */}
              {planAnalytics.length > 0 && (
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4" data-testid="analytics-summary">
                  <div className="bg-white border border-slate-200 rounded-xl p-4 sm:p-5 text-center">
                    <p className="text-xs sm:text-sm text-slate-500 mb-1">Total Users</p>
                    <p className="text-2xl sm:text-3xl font-bold text-slate-900">{planAnalytics.reduce((sum, p) => sum + p.users_count, 0)}</p>
                  </div>
                  <div className="bg-white border border-slate-200 rounded-xl p-4 sm:p-5 text-center">
                    <p className="text-xs sm:text-sm text-slate-500 mb-1">Total Tasks</p>
                    <p className="text-2xl sm:text-3xl font-bold text-slate-900">{planAnalytics.reduce((sum, p) => sum + p.total_user_tasks, 0)}</p>
                  </div>
                  <div className="bg-white border border-blue-200 rounded-xl p-4 sm:p-5 text-center bg-blue-50">
                    <p className="text-xs sm:text-sm text-blue-700 mb-1">Total Hours Logged</p>
                    <p className="text-2xl sm:text-3xl font-bold text-blue-800">{planAnalytics.reduce((sum, p) => sum + (p.total_hours_logged || 0), 0).toFixed(1)}h</p>
                  </div>
                  <div className="bg-white border border-slate-200 rounded-xl p-4 sm:p-5 text-center">
                    <p className="text-xs sm:text-sm text-slate-500 mb-1">Avg Completion</p>
                    <p className="text-2xl sm:text-3xl font-bold text-green-700">
                      {planAnalytics.length > 0 ? (planAnalytics.reduce((sum, p) => sum + p.completion_rate, 0) / planAnalytics.length).toFixed(1) : 0}%
                    </p>
                  </div>
                </div>
              )}

              {/* Study Plans Analytics */}
              <div className="bg-white border border-slate-200 rounded-xl p-4 sm:p-6">
                <h2 className="text-xl sm:text-2xl font-bold text-slate-900 mb-6" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  Study Plans Performance
                </h2>
                {planAnalytics.length === 0 ? (
                  <div className="text-center py-12">
                    <p className="text-slate-500">No analytics data available yet.</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {planAnalytics.map((plan) => (
                      <div key={plan.plan_id} className="border border-slate-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                        <h3 className="font-semibold text-lg mb-3">{plan.plan_name}</h3>
                        <div className="space-y-2 text-sm">
                          <div className="flex justify-between">
                            <span className="text-slate-600">Users:</span>
                            <span className="font-semibold">{plan.users_count}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-slate-600">Tasks:</span>
                            <span className="font-semibold">{plan.total_tasks}</span>
                          </div>
                          <div className="mt-3">
                            <div className="flex justify-between mb-1">
                              <span className="text-slate-600">Completion Rate:</span>
                              <span className="font-semibold text-blue-600">{plan.completion_rate}%</span>
                            </div>
                            <div className="w-full bg-slate-200 rounded-full h-2">
                              <div 
                                className="bg-blue-600 h-2 rounded-full transition-all" 
                                style={{ width: `${plan.completion_rate}%` }}
                              ></div>
                            </div>
                          </div>
                          <div className="flex justify-between pt-2 border-t border-slate-200">
                            <span className="text-slate-600">Total Hours:</span>
                            <span className="font-semibold text-blue-700">{plan.total_hours_logged || 0}h</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-slate-600">Avg Hours/Task:</span>
                            <span className="font-semibold">{plan.avg_hours_logged}h</span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Task Analytics */}
              <div className="bg-white border border-slate-200 rounded-xl p-6">
                <h2 className="text-2xl font-bold text-slate-900 mb-6" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  Task Completion Rankings
                </h2>
                {taskAnalytics.length === 0 ? (
                  <div className="text-center py-12">
                    <p className="text-slate-500">No task data available.</p>
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Rank</TableHead>
                        <TableHead>Task Name</TableHead>
                        <TableHead>Study Plan</TableHead>
                        <TableHead>Completion Rate</TableHead>
                        <TableHead>Avg Hours</TableHead>
                        <TableHead>Total Hours</TableHead>
                        <TableHead>Total Users</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {taskAnalytics.map((task, idx) => (
                        <TableRow key={task.task_id}>
                          <TableCell>
                            <span className={`font-bold ${idx < 3 ? 'text-blue-600' : 'text-slate-600'}`}>
                              #{idx + 1}
                            </span>
                          </TableCell>
                          <TableCell className="font-medium">{task.task_name}</TableCell>
                          <TableCell className="text-sm text-slate-600">{task.plan_name}</TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <div className="w-24 bg-slate-200 rounded-full h-2">
                                <div 
                                  className={`h-2 rounded-full ${(() => {
                                    if (task.completion_rate >= 75) return 'bg-green-500';
                                    if (task.completion_rate >= 50) return 'bg-blue-500';
                                    if (task.completion_rate >= 25) return 'bg-yellow-500';
                                    return 'bg-red-500';
                                  })()}`}
                                  style={{ width: `${task.completion_rate}%` }}
                                ></div>
                              </div>
                              <span className="text-sm font-semibold">{task.completion_rate}%</span>
                            </div>
                          </TableCell>
                          <TableCell>{task.avg_hours}h</TableCell>
                          <TableCell className="font-semibold text-blue-700">{task.total_hours || 0}h</TableCell>
                          <TableCell>
                            <span className="text-sm text-slate-600">
                              {task.complete_count}/{task.total_users}
                            </span>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </div>

              {/* User Tasks Completed vs Open */}
              <div className="bg-white border border-slate-200 rounded-xl p-4 sm:p-6">
                <h2 className="text-xl sm:text-2xl font-bold text-slate-900 mb-6" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  User Tasks: Completed vs Open
                </h2>
                {userTaskAnalytics.length === 0 ? (
                  <div className="text-center py-12">
                    <p className="text-slate-500">No user task data available.</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {userTaskAnalytics.map((u) => (
                      <div key={u.user_id} className="border border-slate-200 rounded-lg overflow-hidden">
                        <div
                          className="flex flex-col sm:flex-row sm:items-center justify-between p-3 sm:p-4 cursor-pointer hover:bg-slate-50 transition-colors gap-2"
                          onClick={() => setExpandedUserId(expandedUserId === u.user_id ? null : u.user_id)}
                          data-testid={`user-analytics-row-${u.user_id}`}
                        >
                          <div className="flex items-center gap-2 sm:gap-4 flex-wrap">
                            <div className="min-w-0">
                              <span className="font-semibold text-slate-900 text-sm sm:text-base">{u.user_name}</span>
                              <span className="text-xs sm:text-sm text-slate-500 ml-2 hidden sm:inline">({u.user_email})</span>
                            </div>
                            <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-blue-100 text-blue-800 border border-blue-200">
                              {u.plan_name}
                            </span>
                          </div>
                          <div className="flex items-center gap-3 sm:gap-6">
                            <div className="flex items-center gap-2 sm:gap-3 text-xs sm:text-sm">
                              <span className="flex items-center gap-1">
                                <span className="w-2 h-2 sm:w-2.5 sm:h-2.5 rounded-full bg-green-500 inline-block"></span>
                                {u.complete_count}
                              </span>
                              <span className="flex items-center gap-1">
                                <span className="w-2 h-2 sm:w-2.5 sm:h-2.5 rounded-full bg-blue-500 inline-block"></span>
                                {u.open_count}
                              </span>
                              <span className="text-xs font-semibold text-blue-700">{u.total_hours || 0}h</span>
                            </div>
                            <div className="w-20 sm:w-32">
                              <div className="flex items-center gap-2">
                                <div className="flex-1 bg-slate-200 rounded-full h-2 overflow-hidden">
                                  <div className="bg-green-500 h-2 rounded-full" style={{ width: `${u.completion_rate}%` }}></div>
                                </div>
                                <span className="text-xs font-semibold text-slate-700">{u.completion_rate}%</span>
                              </div>
                            </div>
                            <span className="text-slate-400 text-xs sm:text-sm">{expandedUserId === u.user_id ? '▲' : '▼'}</span>
                          </div>
                        </div>

                        {expandedUserId === u.user_id && u.tasks.length > 0 && (
                          <div className="border-t border-slate-200 bg-slate-50 px-2 sm:px-4 py-3 overflow-x-auto">
                            <Table>
                              <TableHeader>
                                <TableRow>
                                  <TableHead>Task Name</TableHead>
                                  <TableHead>Status</TableHead>
                                  <TableHead>Due Date</TableHead>
                                  <TableHead>Hours Logged</TableHead>
                                </TableRow>
                              </TableHeader>
                              <TableBody>
                                {u.tasks.map((t) => (
                                  <TableRow key={t.task_id}>
                                    <TableCell className="font-medium">{t.activity}</TableCell>
                                    <TableCell>
                                      <span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold ${
                                        t.status === 'complete'
                                          ? 'bg-green-100 text-green-800 border border-green-200'
                                          : 'bg-blue-100 text-blue-800 border border-blue-200'
                                      }`}>
                                        {t.status}
                                      </span>
                                    </TableCell>
                                    <TableCell>{t.due_date || 'Not set'}</TableCell>
                                    <TableCell>{t.hours_log}h</TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </TabsContent>

          <TabsContent value="milestones">
            <div className="bg-white border border-slate-200 rounded-xl p-6">
              <div className="flex justify-between items-center mb-6">
                <div>
                  <h2 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    Milestones
                  </h2>
                  {selectedPlanId && (
                    <p className="text-sm text-slate-600 mt-1">
                      For: {studyPlans.find(p => p.id === selectedPlanId)?.name}
                    </p>
                  )}
                </div>
                <Button
                  onClick={() => setShowCreateMilestoneDialog(true)}
                  className="bg-blue-600 hover:bg-blue-700"
                  disabled={!selectedPlanId}
                  data-testid="create-milestone-button"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add Milestone
                </Button>
              </div>

              {(() => {
                if (!selectedPlanId) return (
                  <div className="text-center py-12">
                    <p className="text-slate-500">Select a study plan from the Plans tab first</p>
                  </div>
                );
                if (milestones.length === 0) return (
                  <div className="text-center py-12">
                    <p className="text-slate-500">No milestones yet. Add your first milestone!</p>
                  </div>
                );
                return (
                <div className="space-y-3">
                  {milestones.map((ms, idx) => (
                    <div
                      key={ms.id}
                      className="flex items-center gap-4 border border-slate-200 rounded-lg p-4 hover:bg-slate-50 transition-colors"
                      data-testid={`milestone-row-${ms.id}`}
                    >
                      <div className="flex items-center justify-center w-8 h-8 rounded-full bg-blue-100 text-blue-700 font-bold text-sm shrink-0">
                        {ms.order || idx + 1}
                      </div>
                      <div className="flex-1 min-w-0">
                        <h4 className="font-semibold text-slate-900">{ms.title}</h4>
                        {ms.description && (
                          <p className="text-sm text-slate-500 truncate">{ms.description}</p>
                        )}
                      </div>
                      <div className="flex gap-2 shrink-0">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => openEditMilestoneDialog(ms)}
                          data-testid={`edit-milestone-${ms.id}`}
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-red-600 hover:text-red-700"
                          onClick={() => handleDeleteMilestone(ms.id)}
                          data-testid={`delete-milestone-${ms.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
                );
              })()}
            </div>
          </TabsContent>

          <TabsContent value="notes">
            <div className="bg-white border border-slate-200 rounded-xl p-6">
              <div className="flex justify-between items-center mb-6">
                <div>
                  <h2 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    Welcome Notes
                  </h2>
                  {selectedPlanId && (
                    <p className="text-sm text-slate-600 mt-1">
                      For: {studyPlans.find(p => p.id === selectedPlanId)?.name}
                    </p>
                  )}
                  <p className="text-xs text-slate-500 mt-1">This will appear as a "Welcome!" section on the user's dashboard for this plan.</p>
                </div>
              </div>

              {!selectedPlanId ? (
                <div className="text-center py-12">
                  <p className="text-slate-500">Select a study plan from the Plans tab first</p>
                </div>
              ) : (
                <div className="space-y-4">
                  <RichTextEditor
                    value={welcomeNotes}
                    onChange={setWelcomeNotes}
                    placeholder="Write a welcome letter or notes for users assigned to this study plan..."
                    minHeight="250px"
                    maxHeight="500px"
                  />
                  <div className="flex items-center justify-between">
                    <span className={`text-xs ${countWordsFromHtml(welcomeNotes) > 3000 ? 'text-red-600 font-semibold' : 'text-slate-400'}`}>
                      {countWordsFromHtml(welcomeNotes).toLocaleString()} / 3,000 words
                    </span>
                    <Button
                      onClick={handleSaveWelcomeNotes}
                      disabled={welcomeNotesLoading || countWordsFromHtml(welcomeNotes) > 3000}
                      className="bg-blue-600 hover:bg-blue-700"
                      data-testid="save-welcome-notes"
                    >
                      {welcomeNotesLoading ? 'Saving...' : 'Save Notes'}
                    </Button>
                  </div>
                  <div className="bg-amber-50 border border-amber-200 p-3 rounded-lg">
                    <p className="text-xs text-amber-800">Note: Welcome notes auto-hide after the user's 5th login. Users can also dismiss it manually.</p>
                  </div>
                </div>
              )}
            </div>
          </TabsContent>



          <TabsContent value="settings">
            <div className="bg-white border border-slate-200 rounded-xl p-6">
              <h2 className="text-2xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Settings
              </h2>
              <p className="text-sm text-slate-500 mb-6">Customize the user dashboard header appearance.</p>

              <div className="border border-slate-200 rounded-lg p-6">
                <h3 className="text-lg font-semibold text-slate-900 mb-4">Header</h3>

                {/* Logo Upload */}
                <div className="mb-6">
                  <Label className="mb-2 block">Logo (JPEG or PNG)</Label>
                  <div className="flex items-center gap-4">
                    {logoPreview ? (
                      <div className="relative">
                        <div
                          className="rounded-lg border border-slate-200 flex items-center justify-center overflow-hidden"
                          style={{ backgroundColor: headerSettings.logo_bg_color, width: 80, height: 80 }}
                        >
                          <img src={logoPreview} alt="Logo" className="max-w-full max-h-full object-contain" />
                        </div>
                        <button
                          onClick={handleRemoveLogo}
                          className="absolute -top-2 -right-2 w-5 h-5 rounded-full bg-red-500 text-white flex items-center justify-center text-xs hover:bg-red-600"
                          data-testid="remove-logo-button"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                    ) : (
                      <div className="w-20 h-20 rounded-lg border-2 border-dashed border-slate-300 flex items-center justify-center text-slate-400">
                        <Upload className="w-6 h-6" />
                      </div>
                    )}
                    <div>
                      <label className="cursor-pointer">
                        <input
                          type="file"
                          accept="image/jpeg,image/png"
                          onChange={handleLogoUpload}
                          className="hidden"
                          data-testid="logo-upload-input"
                        />
                        <span className="inline-flex items-center px-4 py-2 rounded-md border border-slate-200 text-sm font-medium text-slate-700 hover:bg-slate-50 transition-colors">
                          <Upload className="w-4 h-4 mr-2" />
                          {logoPreview ? 'Change Logo' : 'Upload Logo'}
                        </span>
                      </label>
                      <p className="text-xs text-slate-500 mt-1">Max recommended: 300x250px</p>
                    </div>
                  </div>
                </div>

                {/* Logo Size */}
                <div className="mb-6">
                  <Label htmlFor="logo-size" className="mb-2 block">Logo Size: {headerSettings.logo_size}px</Label>
                  <div className="flex items-center gap-4">
                    <span className="text-xs text-slate-500">40px</span>
                    <input
                      id="logo-size"
                      type="range"
                      min="40"
                      max="300"
                      value={headerSettings.logo_size}
                      onChange={(e) => setHeaderSettings(prev => ({ ...prev, logo_size: parseInt(e.target.value) }))}
                      className="flex-1 h-2 bg-slate-200 rounded-lg appearance-none cursor-pointer accent-blue-600"
                      data-testid="logo-size-slider"
                    />
                    <span className="text-xs text-slate-500">300px</span>
                    <Input
                      type="number"
                      min="40"
                      max="300"
                      value={headerSettings.logo_size}
                      onChange={(e) => setHeaderSettings(prev => ({ ...prev, logo_size: Math.min(300, Math.max(40, parseInt(e.target.value) || 80)) }))}
                      className="w-20 font-mono text-sm"
                      data-testid="logo-size-input"
                    />
                  </div>
                </div>

                {/* Color Pickers */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                  <div>
                    <Label htmlFor="logo-bg-color" className="mb-2 block">Logo Background Color</Label>
                    <div className="flex items-center gap-3">
                      <input
                        id="logo-bg-color"
                        type="color"
                        value={headerSettings.logo_bg_color}
                        onChange={(e) => setHeaderSettings(prev => ({ ...prev, logo_bg_color: e.target.value }))}
                        className="w-10 h-10 rounded border border-slate-200 cursor-pointer"
                        data-testid="logo-bg-color-input"
                      />
                      <Input
                        value={headerSettings.logo_bg_color}
                        onChange={(e) => setHeaderSettings(prev => ({ ...prev, logo_bg_color: e.target.value }))}
                        className="w-32 font-mono text-sm"
                        data-testid="logo-bg-color-text"
                      />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="text-color" className="mb-2 block">Header Text Color</Label>
                    <div className="flex items-center gap-3">
                      <input
                        id="text-color"
                        type="color"
                        value={headerSettings.text_color}
                        onChange={(e) => setHeaderSettings(prev => ({ ...prev, text_color: e.target.value }))}
                        className="w-10 h-10 rounded border border-slate-200 cursor-pointer"
                        data-testid="text-color-input"
                      />
                      <Input
                        value={headerSettings.text_color}
                        onChange={(e) => setHeaderSettings(prev => ({ ...prev, text_color: e.target.value }))}
                        className="w-32 font-mono text-sm"
                        data-testid="text-color-text"
                      />
                    </div>
                  </div>
                </div>

                {/* Preview */}
                <div className="mb-6">
                  <Label className="mb-2 block">Preview (User Dashboard Header)</Label>
                  <div
                    className="rounded-lg border border-slate-200 flex items-center px-4 gap-3"
                    style={{
                      backgroundColor: headerSettings.logo_bg_color,
                      height: Math.max(64, headerSettings.logo_size + 16)
                    }}
                    data-testid="header-preview"
                  >
                    {logoPreview && (
                      <img
                        src={logoPreview}
                        alt="Logo Preview"
                        className="object-contain rounded"
                        style={{ height: headerSettings.logo_size, maxWidth: headerSettings.logo_size * 1.2 }}
                      />
                    )}
                    <span className="text-lg font-bold" style={{ color: headerSettings.text_color, fontFamily: 'Manrope, sans-serif' }}>
                      StudyPlan Pro
                    </span>
                  </div>
                </div>

                <Button
                  onClick={handleSaveHeaderSettings}
                  className="bg-blue-600 hover:bg-blue-700"
                  data-testid="save-header-settings"
                >
                  Save Header Settings
                </Button>
              </div>

              {/* Sign-in Page Image */}
              <div className="border border-slate-200 rounded-lg p-6 mt-6">
                <h3 className="text-lg font-semibold text-slate-900 mb-2">Sign-In Page Photo</h3>
                <p className="text-sm text-slate-500 mb-4">This image appears on the right side of the sign-in page.</p>

                <div className="flex items-start gap-6">
                  {signinImagePreview ? (
                    <div className="relative">
                      <img
                        src={signinImagePreview}
                        alt="Sign-in background"
                        className="w-48 h-32 object-cover rounded-lg border border-slate-200"
                        data-testid="signin-image-preview"
                      />
                      <button
                        onClick={handleRemoveSigninImage}
                        className="absolute -top-2 -right-2 w-5 h-5 rounded-full bg-red-500 text-white flex items-center justify-center text-xs hover:bg-red-600"
                        data-testid="remove-signin-image-button"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  ) : (
                    <div className="w-48 h-32 rounded-lg border-2 border-dashed border-slate-300 flex items-center justify-center text-slate-400">
                      <Upload className="w-6 h-6" />
                    </div>
                  )}
                  <div>
                    <label className="cursor-pointer">
                      <input
                        type="file"
                        accept="image/jpeg,image/png"
                        onChange={handleSigninImageUpload}
                        className="hidden"
                        data-testid="signin-image-upload-input"
                      />
                      <span className="inline-flex items-center px-4 py-2 rounded-md border border-slate-200 text-sm font-medium text-slate-700 hover:bg-slate-50 transition-colors">
                        <Upload className="w-4 h-4 mr-2" />
                        {signinImagePreview ? 'Change Photo' : 'Upload Photo'}
                      </span>
                    </label>
                    <p className="text-xs text-slate-500 mt-1">JPEG or PNG. Recommended: 1200x800px or larger.</p>
                  </div>
                </div>
              </div>

              {/* Sign-in Page Logo */}
              <div className="border border-slate-200 rounded-lg p-6 mt-6">
                <h3 className="text-lg font-semibold text-slate-900 mb-2">Sign-In Page Logo</h3>
                <p className="text-sm text-slate-500 mb-4">This logo appears above the title on the sign-in page. Separate from the user header logo.</p>

                <div className="flex items-start gap-6">
                  {signinLogoPreview ? (
                    <div className="relative">
                      <img
                        src={signinLogoPreview}
                        alt="Sign-in logo"
                        className="h-20 object-contain rounded-lg border border-slate-200"
                        data-testid="signin-logo-preview"
                      />
                      <button
                        onClick={handleRemoveSigninLogo}
                        className="absolute -top-2 -right-2 w-5 h-5 rounded-full bg-red-500 text-white flex items-center justify-center text-xs hover:bg-red-600"
                        data-testid="remove-signin-logo-button"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  ) : (
                    <div className="w-20 h-20 rounded-lg border-2 border-dashed border-slate-300 flex items-center justify-center text-slate-400">
                      <Upload className="w-6 h-6" />
                    </div>
                  )}
                  <div>
                    <label className="cursor-pointer">
                      <input
                        type="file"
                        accept="image/jpeg,image/png"
                        onChange={handleSigninLogoUpload}
                        className="hidden"
                        data-testid="signin-logo-upload-input"
                      />
                      <span className="inline-flex items-center px-4 py-2 rounded-md border border-slate-200 text-sm font-medium text-slate-700 hover:bg-slate-50 transition-colors">
                        <Upload className="w-4 h-4 mr-2" />
                        {signinLogoPreview ? 'Change Logo' : 'Upload Logo'}
                      </span>
                    </label>
                    <p className="text-xs text-slate-500 mt-1">JPEG or PNG. Recommended: 300x250px or smaller.</p>
                  </div>
                </div>
              </div>

              {/* Welcome Email Template */}
              <div className="border border-slate-200 rounded-lg p-6 mt-6" data-testid="welcome-email-section">
                <h3 className="text-lg font-semibold text-slate-900 mb-2">Welcome Email Template</h3>
                <p className="text-sm text-slate-500 mb-4">
                  Configure the welcome email sent to new users when created by admin. Use placeholders: <code className="bg-slate-100 px-1 py-0.5 rounded text-xs">[User Name]</code>, <code className="bg-slate-100 px-1 py-0.5 rounded text-xs">[User Email]</code>, <code className="bg-slate-100 px-1 py-0.5 rounded text-xs">[Password]</code>. For bold text use <code className="bg-slate-100 px-1 py-0.5 rounded text-xs">**text**</code>. For bullet points start a line with <code className="bg-slate-100 px-1 py-0.5 rounded text-xs">- </code>.
                </p>

                <div className="space-y-4">
                  <div>
                    <Label htmlFor="welcome-subject" className="mb-2 block">Email Subject Line</Label>
                    <Input
                      id="welcome-subject"
                      value={welcomeEmailSubject}
                      onChange={(e) => setWelcomeEmailSubject(e.target.value)}
                      placeholder="Welcome to StudyPlan Pro!"
                      data-testid="welcome-email-subject"
                    />
                  </div>
                  <div>
                    <Label htmlFor="welcome-body" className="mb-2 block">Email Body</Label>
                    <RichTextEditor
                      value={welcomeEmailBody}
                      onChange={setWelcomeEmailBody}
                      placeholder="Dear [User Name], Welcome to StudyPlan Pro!"
                      minHeight="300px"
                      maxHeight="500px"
                    />
                    <p className="text-xs text-slate-400 mt-1">The header logo will be automatically added to the top of the email. Use placeholders: [User Name], [User Email], [Password].</p>
                  </div>
                  <Button
                    onClick={handleSaveWelcomeEmail}
                    disabled={welcomeEmailLoading}
                    className="bg-blue-600 hover:bg-blue-700"
                    data-testid="save-welcome-email"
                  >
                    {welcomeEmailLoading ? 'Saving...' : 'Save Welcome Email Template'}
                  </Button>

                  {/* Send Email Letter */}
                  <div className="border-t border-slate-200 pt-4 mt-4">
                    <h4 className="text-sm font-semibold text-slate-900 mb-3">Send Email Letter</h4>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
                      <div>
                        <Label htmlFor="test-email-name" className="mb-1 block text-xs">Recipient Name</Label>
                        <Input
                          id="test-email-name"
                          value={testEmailName}
                          onChange={(e) => setTestEmailName(e.target.value)}
                          placeholder="User Name"
                          data-testid="test-email-name"
                        />
                      </div>
                      <div>
                        <Label htmlFor="test-email-address" className="mb-1 block text-xs">Recipient Email</Label>
                        <Input
                          id="test-email-address"
                          type="email"
                          value={testEmailAddress}
                          onChange={(e) => setTestEmailAddress(e.target.value)}
                          placeholder="user@example.com"
                          data-testid="test-email-address"
                        />
                      </div>
                    </div>
                    <Button
                      onClick={handleSendTestEmail}
                      disabled={welcomeEmailLoading || !testEmailAddress.trim()}
                      variant="outline"
                      className="text-blue-600 hover:text-blue-700"
                      data-testid="send-email-letter"
                    >
                      {welcomeEmailLoading ? 'Sending...' : 'Send Email Letter'}
                    </Button>
                    <p className="text-xs text-slate-400 mt-2">Sends the welcome email template populated with the user's actual data from the system.</p>
                  </div>
                </div>
              </div>

              {/* Email Logs */}
              <div className="border border-slate-200 rounded-lg p-6 mt-6" data-testid="email-logs-section">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h3 className="text-lg font-semibold text-slate-900">Email Log</h3>
                    <p className="text-sm text-slate-500">History of welcome emails sent to users.</p>
                  </div>
                  <Button variant="outline" size="sm" onClick={fetchEmailLogs} data-testid="refresh-email-logs">
                    Refresh
                  </Button>
                </div>
                {emailLogs.length === 0 ? (
                  <p className="text-sm text-slate-400 text-center py-6">No emails sent yet.</p>
                ) : (
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Date</TableHead>
                          <TableHead>Type</TableHead>
                          <TableHead>To</TableHead>
                          <TableHead>Subject</TableHead>
                          <TableHead>Status</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {emailLogs.map((log) => (
                          <TableRow key={log.id}>
                            <TableCell className="text-sm whitespace-nowrap">
                              {log.sent_at ? new Date(log.sent_at).toLocaleString() : 'N/A'}
                            </TableCell>
                            <TableCell>
                              <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                                log.type === 'welcome' ? 'bg-blue-50 text-blue-700 border border-blue-200'
                                : log.type === 'admin_notification' ? 'bg-purple-50 text-purple-700 border border-purple-200'
                                : log.type === 'access_pending' ? 'bg-amber-50 text-amber-700 border border-amber-200'
                                : 'bg-slate-50 text-slate-600 border border-slate-200'
                              }`}>
                                {log.type === 'welcome' ? 'Welcome' : log.type === 'admin_notification' ? 'Admin Alert' : log.type === 'access_pending' ? 'Access Pending' : log.type}
                              </span>
                            </TableCell>
                            <TableCell className="text-sm">
                              <div>{log.to_name}</div>
                              <div className="text-xs text-slate-400">{log.to_email}</div>
                            </TableCell>
                            <TableCell className="text-sm max-w-xs truncate">{log.subject}</TableCell>
                            <TableCell>
                              <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
                                log.status === 'sent'
                                  ? 'bg-green-100 text-green-800 border border-green-200'
                                  : 'bg-red-100 text-red-800 border border-red-200'
                              }`} data-testid={`email-log-status-${log.id}`}>
                                {log.status}
                              </span>
                              {log.error && <p className="text-xs text-red-500 mt-1">{log.error}</p>}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </div>

              {/* Login Activity Log */}
              <div className="border border-slate-200 rounded-lg p-6 mt-6" data-testid="login-logs-section">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h3 className="text-lg font-semibold text-slate-900">Recent Login Activity</h3>
                    <p className="text-sm text-slate-500">Users who recently logged in.</p>
                  </div>
                  <Button variant="outline" size="sm" onClick={fetchLoginLogs} data-testid="refresh-login-logs">Refresh</Button>
                </div>
                {loginLogs.length === 0 ? (
                  <p className="text-sm text-slate-400">No login activity recorded yet.</p>
                ) : (
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Date</TableHead>
                          <TableHead>Name</TableHead>
                          <TableHead>Email</TableHead>
                          <TableHead>Role</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {loginLogs.map((log) => (
                          <TableRow key={log.id}>
                            <TableCell className="text-sm whitespace-nowrap">
                              {log.logged_in_at ? new Date(log.logged_in_at).toLocaleString() : 'N/A'}
                            </TableCell>
                            <TableCell className="font-medium text-sm">{log.name}</TableCell>
                            <TableCell className="text-sm text-slate-600">{log.email}</TableCell>
                            <TableCell>
                              <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                                log.role === 'admin' ? 'bg-purple-50 text-purple-700 border border-purple-200'
                                : 'bg-blue-50 text-blue-700 border border-blue-200'
                              }`}>
                                {log.role}
                              </span>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </main>

      <CreatePlanDialog open={showCreatePlanDialog} onOpenChange={setShowCreatePlanDialog} planFormData={planFormData} setPlanFormData={setPlanFormData} onSubmit={handleCreateStudyPlan} />
      <EditPlanDialog open={showEditPlanDialog} onOpenChange={(v) => { setShowEditPlanDialog(v); if (!v) { setPlanToEdit(null); resetPlanForm(); } }} planFormData={planFormData} setPlanFormData={setPlanFormData} onSubmit={handleUpdateStudyPlan} />
      <ClonePlanDialog open={showClonePlanDialog} onOpenChange={setShowClonePlanDialog} planToClone={planToClone} cloneFormData={cloneFormData} setCloneFormData={setCloneFormData} onSubmit={handleCloneStudyPlan} />
      <TaskFormDialog open={showCreateTaskDialog} onOpenChange={setShowCreateTaskDialog} title="Create Task Template" taskFormData={taskFormData} setTaskFormData={setTaskFormData} onSubmit={handleCreateTask} submitLabel="Create Task" isEdit={false} />
      <TaskFormDialog open={showEditTaskDialog} onOpenChange={setShowEditTaskDialog} title="Edit Task Template" taskFormData={taskFormData} setTaskFormData={setTaskFormData} onSubmit={handleUpdateTask} submitLabel="Update Task" isEdit={true} />
      <AssignPlanDialog open={showAssignDialog} onOpenChange={setShowAssignDialog} users={users} studyPlans={studyPlans} selectedUserId={selectedUserId} setSelectedUserId={setSelectedUserId} selectedPlanForAssign={selectedPlanForAssign} setSelectedPlanForAssign={setSelectedPlanForAssign} onSubmit={handleAssignPlan} />
      <ManagePlansDialog open={showManagePlansDialog} onOpenChange={setShowManagePlansDialog} user={managePlansUser} onRemovePlan={handleRemovePlan} onEditStartDate={(user, plan) => { setShowManagePlansDialog(false); openStartDateDialog(user, plan); }} onEditExpiration={(user, plan) => { setShowManagePlansDialog(false); openExpirationDialog(user, plan); }} />
      <AdminStartDateDialog open={showStartDateDialog} onOpenChange={setShowStartDateDialog} user={userToEditStartDate} planName={editingPlanName} newStartDate={newStartDate} setNewStartDate={setNewStartDate} onSubmit={handleUpdateUserStartDate} />
      <MilestoneDialog open={showCreateMilestoneDialog} onOpenChange={setShowCreateMilestoneDialog} title="Add Milestone" milestoneFormData={milestoneFormData} setMilestoneFormData={setMilestoneFormData} onSubmit={handleCreateMilestone} submitLabel="Add Milestone" isEdit={false} />
      <MilestoneDialog open={showEditMilestoneDialog} onOpenChange={setShowEditMilestoneDialog} title="Edit Milestone" milestoneFormData={milestoneFormData} setMilestoneFormData={setMilestoneFormData} onSubmit={handleUpdateMilestone} submitLabel="Update Milestone" isEdit={true} />
      <ExpirationDialog open={showExpirationDialog} onOpenChange={setShowExpirationDialog} user={expirationUser} planName={editingPlanName} expirationDate={expirationDate} setExpirationDate={setExpirationDate} onSubmit={handleSetExpiration} onClear={() => { setExpirationDate(''); handleSetExpiration(); }} />
      <BulkExpirationDialog open={showBulkExpirationDialog} onOpenChange={setShowBulkExpirationDialog} studyPlans={studyPlans} bulkExpirationPlanId={bulkExpirationPlanId} setBulkExpirationPlanId={setBulkExpirationPlanId} bulkExpirationDate={bulkExpirationDate} setBulkExpirationDate={setBulkExpirationDate} onSubmit={handleBulkExpiration} onClear={() => { setBulkExpirationDate(''); handleBulkExpiration(); }} />
      <ResetPasswordDialog open={showResetPasswordDialog} onOpenChange={setShowResetPasswordDialog} user={resetPasswordUser} password={adminNewPassword} setPassword={setAdminNewPassword} onSubmit={handleAdminResetPassword} />
      <CreateUserDialog open={showCreateUserDialog} onOpenChange={setShowCreateUserDialog} formData={createUserForm} setFormData={setCreateUserForm} onSubmit={handleCreateUser} />

      <Dialog open={showDeleteUserDialog} onOpenChange={setShowDeleteUserDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete User</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-600 py-2">
            Are you sure you want to permanently delete <strong>{deleteUserTarget?.name}</strong> ({deleteUserTarget?.email})? This will remove all their study plan data and cannot be undone.
          </p>
          <div className="flex justify-end gap-3 mt-4">
            <Button variant="outline" onClick={() => setShowDeleteUserDialog(false)} data-testid="cancel-delete-user">Cancel</Button>
            <Button variant="destructive" onClick={handleDeleteUser} data-testid="confirm-delete-user">Delete User</Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={showEditUserDialog} onOpenChange={setShowEditUserDialog}>
        <DialogContent data-testid="edit-user-dialog">
          <DialogHeader>
            <DialogTitle>Edit User</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <Label htmlFor="edit-user-name">Full Name</Label>
              <Input id="edit-user-name" value={editUserName} onChange={(e) => setEditUserName(e.target.value)} data-testid="edit-user-name-input" />
            </div>
            <div>
              <Label htmlFor="edit-user-email">Email</Label>
              <Input id="edit-user-email" type="email" value={editUserEmail} onChange={(e) => setEditUserEmail(e.target.value)} data-testid="edit-user-email-input" />
            </div>
            <div className="flex justify-end gap-3">
              <Button variant="outline" onClick={() => setShowEditUserDialog(false)}>Cancel</Button>
              <Button onClick={handleEditUser} className="bg-blue-600 hover:bg-blue-700" data-testid="save-edit-user">Save Changes</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
